import authService from "@api/authService.js";
import translationService from "@/services/translationService.js";
import router from "@/services/router.js";
import config from "@/config.js";
import { initImmersiveMode } from "@/services/immersiveMode.js";
import { initImageSessionRecovery } from "@/services/imageSessionRecovery.js";
import Header from "@modules/navigation/Header.js";
import Footer from "@modules/navigation/Footer.js";
import galleryController from "@modules/gallery/galleryController.js";
import showcaseController from "@modules/showcase/showcaseController.js";
import ContactForm from "@modules/contact/ContactForm.js";
import CookieBanner from "@modules/cookie/CookieBanner.js";
import AboutPage from "@modules/pages/AboutPage.js";
import PrivacyPage from "@modules/pages/PrivacyPage.js";
import cockpitController from "@modules/cockpit/cockpitController.js";
import { DEFAULT_SECTION_ID } from "@modules/cockpit/cockpitSections.js";

let lang;
let main;
let active = null; // currently mounted route component

function clearOutlet() {
  if (active) {
    active.unmount();
    active = null;
  }
  main.innerHTML = "";
}

function setLanguage(next) {
  localStorage.setItem("language", next);
  window.location.reload();
}

async function bootstrap() {
  lang = localStorage.getItem("language") || config.defaultLanguage;
  document.documentElement.lang = lang;
  initImmersiveMode();
  initImageSessionRecovery();

  // header-root has static skeleton markup in index.html so its fixed-height
  // box is reserved on first paint; footer-root starts empty on purpose (see
  // the router.init() comment below).
  const headerRoot = document.getElementById("header-root");
  main = document.getElementById("main");
  const footerRoot = document.getElementById("footer-root");

  await translationService.load(lang);
  await authService.refresh();

  headerRoot.innerHTML = "";
  await new Header({ lang, onLanguageChange: setLanguage }).mount(headerRoot);

  router
    .add("/", async () => {
      clearOutlet();
      try {
        active = await galleryController.showGrid(main, lang);
      } catch (err) {
        console.error(err);
        main.innerHTML = `<p class="error">${translationService.t("gallery.loadError")}</p>`;
      }
    })
    .add("/gallery/:id", async ({ id }) => {
      clearOutlet();
      active = await galleryController.showDetail(main, id, lang);
    })
    .add("/gear", async () => {
      clearOutlet();
      active = await showcaseController.showPage(main, lang);
    })
    .add("/gear/:id", async ({ id }) => {
      clearOutlet();
      active = await showcaseController.showDetail(main, id, lang);
    })
    .add("/contact", async () => {
      clearOutlet();
      active = new ContactForm();
      await active.mount(main);
    })
    .add("/about", async () => {
      clearOutlet();
      active = new AboutPage({ lang });
      await active.mount(main);
    })
    .add("/privacy", async () => {
      clearOutlet();
      active = new PrivacyPage({ lang });
      await active.mount(main);
    })
    // Admin cockpit. The bare path picks the first registered section so the
    // nav link never has to know which one that is.
    .add("/cockpit", async () => {
      if (!authService.isAdmin()) return router.replace("/");
      router.replace(`/cockpit/${DEFAULT_SECTION_ID}`);
    })
    .add("/cockpit/:section", async ({ section }) => {
      clearOutlet();
      if (!authService.isAdmin()) return router.replace("/");
      active = await cockpitController.showPage(main, lang, section);
    })
    // Paths these pages used to live at. Kept so shared and bookmarked links
    // keep working, and `replace` rather than `go` so Back skips the alias
    // instead of bouncing off it forever (see router.replace's comment).
    .add("/g/:id", ({ id }) => router.replace(`/gallery/${id}`))
    .add("/showcase", () => router.replace("/gear"))
    .setFallback(() => router.go("/"));

  // Wait for the first route's content to land in #main before the footer's
  // first paint, so the footer never has a "previous" position to shift from
  // when async content (e.g. the gallery grid) determines the page height.
  await router.init();

  await new Footer().mount(footerRoot);

  if (CookieBanner.shouldShow()) {
    new CookieBanner().mount(document.body);
  }
}

bootstrap();
